/**
 * TopBar Component
 */
import Clock from '@components/atoms/text/topBarClock';
import Container from '@components/atoms/container/topBarContainer';
import FlavourConfig from '@shared/config/flavours';
import Logo from '@components/atoms/images/topBarLogo';
import Plan from '@components/atoms/text/topBarPlan';
import TranslationManager from '@shared/assets/locale/translationsManager';
import User from '@components/atoms/text/topBarUser';
import UserClass from '@shared/classes/user';
import { appSelector } from '@shared/store/slices/app.slice';
import { selectFocus } from '@shared/store/slices/focus.slice';
import { selectRoute } from '@shared/store/slices/router.slice';
import { useAppSelector } from '@shared/store/hooks';
import { useEffect, useMemo, useState } from 'react';
import {
  selectUserName,
  selectUserPlanId,
} from '@shared/store/slices/user.slice';
import { DURATION } from '@shared/types/constants/animations';
import { TOP_BAR_PAGES_BLACKLIST } from '@shared/types/constants/pageVisibility';

/**
 * TopBar Component that displays the top bar with user greeting, clock, and logo.
 */
export default function TopBar() {
  // Render the top bar
  const { props, plan, userName } = useTopBar();
  return (
    <Container {...props.container}>
      {/* Conditionally render user greeting if user name is available */}
      {userName ? <User>{userName}</User> : null}
      {/* Always render clock */}
      <Clock />
      {/* Always render logo */}
      <Logo margin={false} />
      {/* Conditionally render plan if available */}
      {plan ? <Plan /*color={plan?.colorHex}*/>{plan?.shortName}</Plan> : null}
    </Container>
  );
}

/**
 * Hook to manage the top bar display and content.
 * The visibility of the top bar is conditionally controlled based on:
 * - Mini detail description
 * - Current page
 * - Current focus state
 *
 * @returns {Object} Display state, plan information, and user greeting
 */
function useTopBar() {
  // Selectors
  const {
    status: { description },
  } = useAppSelector(appSelector);
  const { Page } = useAppSelector(selectRoute);
  const focus = useAppSelector(selectFocus);
  const name = useAppSelector(selectUserName);
  const planId = useAppSelector(selectUserPlanId);

  // State
  const [display, setDisplay] = useState(false);

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  /**
   * Determines when the top bar should be displayed.
   *
   * Display conditions:
   * 1. No mini detail description is present
   * 2. Current page is NOT Detail or Search page
   * 3. AND one of the following:
   *    - Focus is on specific UI elements (channel list, menu, sliders, etc.)
   *    - Focus includes RAILS_LIST
   *    - Current page is one of the specified pages (channel list, account, settings, etc.)
   */
  useEffect(() => {
    setDisplay(!description && !TOP_BAR_PAGES_BLACKLIST.includes(Page));
  }, [description, focus, Page]);

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      props: {
        container: {
          display,
          duration: DURATION.TOP_BAR_CONTAINER,
        },
      },
      plan: FlavourConfig.showPlan ? UserClass.getPlan(planId) : null,
      userName: name?.length
        ? `${TranslationManager.get('COMPONENTS.TOP_BAR.HELLO')} ${name},`
        : '',
    };
  }, [display, name, planId]);
}
